import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { map, take } from 'rxjs';

@Component({
  selector: 'sfa-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, AsyncPipe, BsDropdownModule],
  template: `
    <nav
      class="navbar navbar-expand-sm navbar-dark bg-primary navbar-expand-lg"
    >
      <div class="container-fluid">
        <a class="navbar-brand d-flex align-items-center gap-2" routerLink="/">
          <!--
            The AU shield, resampled from the 300 DPI brand master to 84px tall —
            3x the 28px render size, so it stays sharp on HiDPI without shipping
            a 21-megapixel image the browser would hold decoded in memory.
            Transparent background, so it depends on the navbar staying a dark
            colour (bg-primary). width/height reserve the box to avoid a layout
            shift on load; the alt text names the institution the mark stands for,
            which the adjacent "Student Forms Admin" text does not convey.
          -->
          <img
            src="assets/au-shield.png"
            alt="Athabasca University"
            width="22"
            height="28"
            data-cy="au-logo"
          />
          Student Forms Admin
        </a>

        <ul class="navbar-nav text-left" style="width: 100%;">
          <li
            class="nav-item"
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: true }"
          >
            <a class="nav-link" routerLink="/">Home</a>
          </li>
          @if (isAuthenticated$ | async) {
            <li
              class="nav-item"
              routerLinkActive="active"
              data-cy="nav-dashboard-item"
            >
              <a
                class="nav-link"
                routerLink="/dashboard"
                data-cy="nav-dashboard"
                >Dashboard</a
              >
            </li>
            <li
              class="nav-item"
              routerLinkActive="active"
              data-cy="nav-archive-item"
            >
              <a class="nav-link" routerLink="/archive" data-cy="nav-archive"
                >Archive</a
              >
            </li>
            <li
              class="nav-item"
              routerLinkActive="active"
              data-cy="nav-users-item"
            >
              <a class="nav-link" routerLink="/users" data-cy="nav-users"
                >Users</a
              >
            </li>
          }
        </ul>

        <div class="text-white d-flex align-items-right">
          @if (isAuthenticated$ | async) {
            <div class="nav-item dropdown" dropdown>
              <button
                class="btn btn-primary dropdown-toggle"
                dropdownToggle
                type="button"
                aria-expanded="false"
                data-cy="user-menu"
              >
                <i class="bi bi-person"></i> {{ displayName$ | async }}
              </button>

              <ul *dropdownMenu class="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    class="dropdown-item d-flex align-items-center"
                    (click)="logout()"
                    data-cy="logout-button"
                  >
                    <b>Logout</b>
                  </button>
                </li>
              </ul>
            </div>
          } @else {
            <button
              class="btn btn-primary btn-lg"
              (click)="login()"
              data-cy="login-button"
            >
              Login
            </button>
          }
        </div>
      </div>
    </nav>
  `,
  styles: [
    `
      /* Active navigation link styling */
      .nav-item.active .nav-link {
        font-weight: 600;
        color: #fff;
      }
    `,
  ],
})
export class NavbarComponent {
  private oidcService = inject(OidcSecurityService);

  isAuthenticated$ = this.oidcService.isAuthenticated$.pipe(
    map((result) => result.isAuthenticated),
  );
  displayName$ = this.oidcService.userData$.pipe(
    map(
      ({ userData }) =>
        userData?.given_name || userData?.email || userData?.username || '',
    ),
  );

  login(): void {
    this.oidcService.authorize();
  }

  logout(): void {
    this.oidcService.logoff().pipe(take(1)).subscribe();
  }
}
